interface CacheEntry<T> {
  data: T;
  timestamp: number;
  hash: string; // To detect data changes
}

export class CacheManager {
  private static CACHE_DURATION = 60 * 60 * 1000; // 1 hour

  /**
   * Generate a simple hash from data to detect changes
   */
  private static generateHash(data: any): string {
    return JSON.stringify(data);
  }

  /**
   * Store data in cache with timestamp and hash
   */
  static set<T>(key: string, data: T): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      hash: this.generateHash(data),
    };
    try {
      localStorage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.error('Failed to cache data:', error);
    }
  }

  /**
   * Get data from cache if it's still valid
   * Returns null if cache is expired or doesn't exist
   */
  static get<T>(key: string, maxAge: number = this.CACHE_DURATION): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const entry: CacheEntry<T> = JSON.parse(cached);
      const age = Date.now() - entry.timestamp;

      // Check if cache has expired
      if (age > maxAge) {
        this.remove(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.error('Failed to retrieve cached data:', error);
      return null;
    }
  }

  /**
   * Check if new data is different from cached data
   * Returns true if data has changed or cache doesn't exist
   */
  static hasDataChanged<T>(key: string, newData: T): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return true;

      const entry: CacheEntry<T> = JSON.parse(cached);
      const newHash = this.generateHash(newData);

      return entry.hash !== newHash;
    } catch (error) {
      console.error('Failed to check data changes:', error);
      return true;
    }
  }

  /**
   * Remove cache entry
   */
  static remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove cache:', error);
    }
  }

  /**
   * Clear all cache entries (useful for logout or force refresh)
   */
  static clearAll(prefix?: string): void {
    try {
      if (prefix) {
        // Clear only keys with specific prefix
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(prefix)) {
            localStorage.removeItem(key);
          }
        });
      } else {
        // Clear all
        localStorage.clear();
      }
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }

  /**
   * Get cache age in milliseconds
   */
  static getCacheAge(key: string): number | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const entry: CacheEntry<any> = JSON.parse(cached);
      return Date.now() - entry.timestamp;
    } catch (error) {
      console.error('Failed to get cache age:', error);
      return null;
    }
  }

  /**
   * Check if cache is valid (exists and not expired)
   */
  static isValid(key: string, maxAge: number = this.CACHE_DURATION): boolean {
    const age = this.getCacheAge(key);
    return age !== null && age <= maxAge;
  }
}
